import { arrayChunk, createDebouncer, request } from '.';
const { STOREFRONT_ACCESS_TOKEN } = window;

// https://github.com/sindresorhus/p-defer/blob/0abf7f56337a450cbff45074dd8c83f8ace4726c/index.js
const pDefer = () => {
  const deferred = {};

  deferred.promise = new Promise((resolve, reject) => {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });

  return deferred;
};

const STOREFRONT_PRODUCT_QUERY = /* GraphQL */ `
  fragment ProductFields on Product {
    id
    title
    description
    handle
    tags
    onlineStoreUrl
    availableForSale
    productType
    options {
      id
      name
      values
    }
    priceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }

    compareAtPriceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }

    category: metafield(namespace: "custom", key: "category") {
      value
    }

    color: metafield(namespace: "custom", key: "color") {
      value
    }

    images(first: 2) {
      nodes {
        id
        altText
        url
      }
    }

    variants(first: 250) {
      nodes {
        id
        sku
        quantityAvailable
        availableForSale
        title

        price {
          amount
          currencyCode
        }

        compareAtPrice {
          amount
          currencyCode
        }

        selectedOptions {
          name
          value
        }

        image {
          url
        }

        metafield(namespace: "custom", key: "color_swatch") {
          reference {
            ... on Metaobject {
              color_name: field(key: "name") {
                value
              }
              color_code: field(key: "color_code") {
                value
              }
              color_gradient: field(key: "color_gradient") {
                value
              }
              color_image: field(key: "image") {
                reference {
                  ... on MediaImage {
                    image {
                      url
                      altText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const STOREFRONT_PRODUCTS_QUERY = /* GraphQL */ `
  ${STOREFRONT_PRODUCT_QUERY}
  query Products(
    $country: CountryCode!
    $id1: ID!
    $include2: Boolean!
    $id2: ID
    $include3: Boolean!
    $id3: ID
    $include4: Boolean!
    $id4: ID
    $include5: Boolean!
    $id5: ID
    $include6: Boolean!
    $id6: ID
    $include7: Boolean!
    $id7: ID
    $include8: Boolean!
    $id8: ID
    $include9: Boolean!
    $id9: ID
    $include10: Boolean!
    $id10: ID
  ) @inContext(country: $country) {
    p1: product(id: $id1) {
      ...ProductFields
    }
    p2: product(id: $id2) @include(if: $include2) {
      ...ProductFields
    }
    p3: product(id: $id3) @include(if: $include3) {
      ...ProductFields
    }
    p4: product(id: $id4) @include(if: $include4) {
      ...ProductFields
    }
    p5: product(id: $id5) @include(if: $include5) {
      ...ProductFields
    }
    p6: product(id: $id6) @include(if: $include6) {
      ...ProductFields
    }
    p7: product(id: $id7) @include(if: $include7) {
      ...ProductFields
    }
    p8: product(id: $id8) @include(if: $include8) {
      ...ProductFields
    }
    p9: product(id: $id9) @include(if: $include9) {
      ...ProductFields
    }
    p10: product(id: $id10) @include(if: $include10) {
      ...ProductFields
    }
  }
`;

export const productsData = {};
export const variantsData = {};

let productsToFetch = new Set();
const productsInProgress = {};

const fetchDataBackground = async (ids, query, dataObject, inProgressDataObject, processItemFunction) => {
  if (ids.size === 0) {
    // eslint-disable-next-line no-console
    console.warn('No data to fetch');
    return;
  }

  const chunks = arrayChunk([...ids], 10);

  await Promise.all(
    chunks.map(async (chunk) => {
      const variables = new Array(10).fill().reduce((acc, _, index) => {
        const id = chunk[index];
        acc[`id${index + 1}`] = id;
        acc[`include${index + 1}`] = !!id;
        return acc;
      }, {});
      variables.country = Shopify.country;

      let data, error;
      try {
        const res = await fetch(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
          },
          body: JSON.stringify({ query, variables }),
        });

        const { data: d, errors } = await res.json();
        data = d;

        if (errors) {
          throw new Error(`Failed to fetch: ${errors.map((error) => error.message).join(', ')}`);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(error);
        error = err;
      }

      if (!error) {
        Object.values(data).filter(Boolean).forEach(processItemFunction);
      }

      chunk.forEach((id) => {
        const product = dataObject[id];

        if (product) {
          inProgressDataObject[id].resolve(product);
        } else {
          const err = error || new Error(`${id} not found`);
          inProgressDataObject[id].reject(err);
        }

        delete inProgressDataObject[id];
      });
    }),
  );
};

const fetchData = async (ids, prefix, idsToFetchSet, dataObject, inProgressDataObject, fetchBackgroundFunction) => {
  const normalizedIds = ids
    .map((id) => {
      if (typeof id === 'string' && id.startsWith(prefix)) {
        return id;
      }

      if (typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id))) {
        return `${prefix}${id}`;
      }
    })
    .filter(Boolean);

  const idsToFetch = normalizedIds.filter((id) => !dataObject[id] && !inProgressDataObject[id]);

  idsToFetch.forEach((id) => {
    idsToFetchSet.add(id);
    inProgressDataObject[id] = pDefer();
  });

  const idsInProgress = normalizedIds.filter((id) => inProgressDataObject[id]);

  if (idsToFetch.length > 0) {
    fetchBackgroundFunction();
  }

  if (idsInProgress.length > 0) {
    await Promise.all(idsInProgress.map((id) => inProgressDataObject[id].promise));
  }

  return dataObject;
};

const priceFormatters = {};
const getPriceFormatter = (currency) => {
  if (!priceFormatters[currency]) {
    priceFormatters[currency] = new Intl.NumberFormat(Shopify.locale, {
      style: 'currency',
      currency,
    });
  }
  return priceFormatters[currency];
};

export const formatPrice = (price, ignoreZeros = false) => {
  if (ignoreZeros && Number(price.amount) === 0) {
    return '';
  }

  const priceFormatter = getPriceFormatter(price.currencyCode);
  return priceFormatter.format(price.amount);
};

export const formatPriceRange = (min, max, ignoreZeros = false) => {
  if (min.amount === max.amount) {
    return formatPrice(min, ignoreZeros);
  }

  const priceFormatter = getPriceFormatter(min.currencyCode);
  if (priceFormatter.formatRange) {
    return priceFormatter.formatRange(min.amount, max.amount);
  }
  return `${priceFormatter.format(min.amount)} - ${priceFormatter.format(max.amount)}`;
};

const formatProductPrices = (product) => {
  product.priceRangeFormatted = formatPriceRange(
    product.priceRange.minVariantPrice,
    product.priceRange.maxVariantPrice,
  );
  product.compareAtPriceRangeFormatted = formatPriceRange(
    product.compareAtPriceRange.minVariantPrice,
    product.compareAtPriceRange.maxVariantPrice,
    true,
  );
  product.variants.forEach((variant) => {
    variant.priceFormatted = formatPrice(variant.price);
    if (variant.compareAtPrice) {
      variant.compareAtPriceFormatted = formatPrice(variant.compareAtPrice, true);
    }
  });
};

const fetchProductsBackground = async () => {
  const productsToFetchSet = productsToFetch;
  productsToFetch = new Set();

  return fetchDataBackground(
    productsToFetchSet,
    STOREFRONT_PRODUCTS_QUERY,
    productsData,
    productsInProgress,
    (product) => {
      product.variants = product.variants.nodes;
      formatProductPrices(product);
      product.images = product.images.nodes;
      productsData[product.id] = product;
      productsData[product.id.replace('gid://shopify/Product/', '')] = product;
      // product.variants.forEach((variant) => {
      //   variant.product = product;
      //   variantsData[variant.id] = variant;
      //   variantsData[variant.id.replace('gid://shopify/ProductVariant/', '')] = variant;
      // });
    },
  );
};

const fetchProductsBackgroundDebounced = createDebouncer((cb) => setTimeout(cb, 0))(fetchProductsBackground);

export const fetchProducts = async (ids) => {
  // id can be number or gid://shopify/Product/... string

  return fetchData(
    ids,
    'gid://shopify/Product/',
    productsToFetch,
    productsData,
    productsInProgress,
    fetchProductsBackgroundDebounced,
  );
};

const STOREFRONT_PRODUCTS_AND_FILTERS = /* GraphQL */ `
  ${STOREFRONT_PRODUCT_QUERY}

  fragment FilterFields on Filter {
    id
    label
    type
    values {
      id
      label
      count
      input
    }
  }

  fragment PageInfoFields on PageInfo {
    hasPreviousPage
    hasNextPage
    endCursor
  }
`;

const FETCH_COLLECTION_PRODUCTS_QUERY = /* GraphQL */ `
  ${STOREFRONT_PRODUCTS_AND_FILTERS}

  query Collection(
    $handle: String
    $afterCursor: String
    $noOfProducts: Int!
    $filtersArray: [ProductFilter!]
    $sortField: ProductCollectionSortKeys
    $reverse: Boolean
  ) {
    collection(handle: $handle) {
      handle
      products(
        first: $noOfProducts
        after: $afterCursor
        filters: $filtersArray
        sortKey: $sortField
        reverse: $reverse
      ) {
        nodes {
          ...ProductFields
        }
        filters {
          ...FilterFields
        }
        pageInfo {
          ...PageInfoFields
        }
      }
      metafields(identifiers: { key: "collection_cards", namespace: "custom" }) {
        references(first: 10) {
          nodes {
            ... on Metaobject {
              id
              cta_link: field(key: "cta_link") {
                reference {
                  ... on Page {
                    handle
                    onlineStoreUrl
                  }
                }
              }
              cta_text: field(key: "cta_text") {
                value
              }
              block_position: field(key: "block_position") {
                value
              }
              asset: field(key: "asset") {
                reference {
                  ... on MediaImage {
                    image {
                      url
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const FETCH_SEARCH_PRODUCTS_QUERY = /* GraphQL */ `
  ${STOREFRONT_PRODUCTS_AND_FILTERS}

  query Search(
    $query: String!
    $afterCursor: String
    $noOfProducts: Int!
    $filtersArray: [ProductFilter!]
    $sortField: SearchSortKeys
    $reverse: Boolean
  ) {
    products: search(
      query: $query
      first: $noOfProducts
      after: $afterCursor
      productFilters: $filtersArray
      sortKey: $sortField
      reverse: $reverse
      types: [PRODUCT]
      prefix: LAST
    ) {
      nodes {
        ...ProductFields
      }
      productFilters {
        ...FilterFields
      }
      pageInfo {
        ...PageInfoFields
      }
      totalCount
    }
  }
`;

const FETCH_RECOMMENDED_PRODUCTS_QUERY = /* GraphQL */ `
  ${STOREFRONT_PRODUCT_QUERY}

  query ProductRecommendations($intent: ProductRecommendationIntent, $productId: ID!) {
    productRecommendations(intent: $intent, productId: $productId) {
      ...ProductFields
    }
  }
`;

const FETCH_SWATCHES_VALUES_QUERY = `
query {
  metaobjects(type: "color_metaobject", first: 20) {
    nodes {
      color_name: field(key: "name") {
        value
      }
      color_code: field(key: "color_code") {
        value
      }
      color_gradient: field(key: "color_gradient") {
        value
      }
      color_image: field(key: "image") {
        reference {
          ... on MediaImage {
            image {
              url
              altText
            }
          }
        }
      }
      id
    }
  }
}
`;

const fetchColorMetaobjectsQuery = async (query) => {
  const response = await request(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({
      query: query,
    }),
  });
  const { data } = response;
  return data.metaobjects;
};

const fetchProductsWithQuery = async (query, variables, getObjectWithProducts, normalize = () => {}) => {
  const response = await request(`https://${Shopify.shop}/api/2023-07/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({
      query: query,
      variables,
    }),
  });
  const { data } = response;
  const objectWithProducts = getObjectWithProducts(data);
  normalize(objectWithProducts);
  const products = objectWithProducts?.products?.nodes || [];
  products.forEach((product) => {
    product.variants = product.variants.nodes;
    formatProductPrices(product);
    product.images = product.images.nodes;
    productsData[product.id] = product;
    productsData[product.id.replace('gid://shopify/Product/', '')] = product;
  });
  return objectWithProducts;
};

export const fetchCollection = async (
  collectionHandle,
  afterCursor = null,
  noOfProducts = 12,
  filtersArray,
  sortField,
  reverse,
) => {
  return fetchProductsWithQuery(
    FETCH_COLLECTION_PRODUCTS_QUERY,
    {
      handle: collectionHandle,
      afterCursor: afterCursor,
      noOfProducts: noOfProducts,
      filtersArray: filtersArray,
      sortField: sortField,
      reverse: reverse,
    },
    (data) => data.collection,
  );
};

export const fetchSearch = async (
  query,
  afterCursor = null,
  noOfProducts = 12,
  filtersArray = [],
  sortField = 'RELEVANCE',
  reverse = false,
) => {
  return fetchProductsWithQuery(
    FETCH_SEARCH_PRODUCTS_QUERY,
    {
      query,
      afterCursor: afterCursor,
      noOfProducts: noOfProducts,
      filtersArray: filtersArray,
      sortField: sortField,
      reverse: reverse,
    },
    (data) => data,
    (search) => {
      search.products.filters = search.products.productFilters;
      delete search.products.productFilters;
    },
  );
};

export const fetchRecommendedProducts = async (intent, productId) => {
  return fetchProductsWithQuery(
    FETCH_RECOMMENDED_PRODUCTS_QUERY,
    {
      intent,
      productId,
    },
    (data) => ({ products: { nodes: data.productRecommendations || [] } }),
  );
};

export const fetchColorMetaobjects = async () => {
  return fetchColorMetaobjectsQuery(FETCH_SWATCHES_VALUES_QUERY);
};
